import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const StandingsHistoryChart = ({ finalData = [], selectedManager, className = "" }) => {
  // Prepare data for the chart
  const prepareChartData = () => {
    if (!finalData.length || !selectedManager) return [];

    // Get all managers and years
    const allManagers = [...new Set(finalData.map(row => row.Owner))];
    const allYears = [...new Set(finalData.map(row => row.Year))].sort((a, b) => a - b);
    
    // Create chart data structure
    const chartData = allYears.map(year => {
      const yearData = { year };
      
      // Add standings for each manager
      allManagers.forEach(manager => {
        const managerYear = finalData.find(row => row.Year === year && row.Owner === manager);
        if (managerYear) {
          yearData[manager] = managerYear.Standings;
        }
      });
      
      return yearData;
    });

    return chartData;
  };

  // Calculate manager statistics
  const calculateManagerStats = () => {
    if (!finalData.length || !selectedManager) return null;

    const managerData = finalData.filter(row => row.Owner === selectedManager);
    const yearsActive = managerData.length;
    const medianFinish = managerData.length > 0 ? 
      [...managerData.map(row => row.Standings)].sort((a, b) => a - b)[Math.floor(managerData.length / 2)] : 0;

    return { yearsActive, medianFinish };
  };

  const chartData = prepareChartData();
  const stats = calculateManagerStats();

  if (!chartData.length || !stats) {
    return (
      <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Standings History</h3>
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  // Helper function to convert number to ordinal
  const getOrdinal = (num) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return num + "st";
    if (j === 2 && k !== 12) return num + "nd";
    if (j === 3 && k !== 13) return num + "rd";
    return num + "th";
  };

  // Custom tooltip with mobile optimizations
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Get all managers with standings for this year, sorted by position
      const allStandings = payload
        .filter(entry => entry.value !== null && entry.value !== undefined)
        .sort((a, b) => a.value - b.value);
      
      if (allStandings.length === 0) return null;
      
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-xl p-3 sm:p-4 max-w-xs sm:max-w-sm">
          <p className="font-bold text-gray-900 mb-2 sm:mb-3 border-b pb-1 sm:pb-2 text-center text-sm sm:text-base">
            {label} Final Standings
          </p>
          
          <div className="space-y-1 sm:space-y-1.5 max-h-48 sm:max-h-none overflow-y-auto">
            {allStandings.map((entry, index) => (
              <div key={entry.dataKey} className="flex justify-between items-center">
                <span className={`font-medium text-xs sm:text-sm truncate mr-2 ${
                  entry.dataKey === selectedManager ? 'text-purple-600' : 'text-gray-700'
                }`}>
                  {entry.dataKey}
                </span>
                <span className={`font-bold text-xs sm:text-sm flex-shrink-0 ${
                  entry.dataKey === selectedManager ? 'text-purple-600' : 'text-gray-500'
                }`}>
                  {getOrdinal(entry.value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  // Create all manager lines (background)
  const createBackgroundLines = () => {
    const allManagers = [...new Set(finalData.map(row => row.Owner))];
    return allManagers
      .filter(manager => manager !== selectedManager)
      .map((manager, index) => (
        <Line
          key={manager}
          type="monotone"
          dataKey={manager}
          stroke="#d1d5db"
          strokeWidth={2}
          strokeOpacity={0.3}
          dot={{ 
            fill: '#d1d5db', 
            strokeWidth: 1, 
            stroke: '#ffffff',
            r: 3, // Slightly smaller on mobile
            fillOpacity: 0.6
          }}
          activeDot={{ 
            r: 5, // Smaller active dots for mobile
            stroke: '#6b7280', 
            strokeWidth: 2,
            fill: '#ffffff',
            fillOpacity: 1
          }}
          connectNulls={false}
        />
      ));
  };

  return (
    <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
      {/* Title and Stats - Mobile optimized */}
      <div className="mb-4 sm:mb-6">
        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 leading-tight">
          <span className="hidden sm:inline">History of where {selectedManager} finished in the standings</span>
          <span className="sm:hidden">{selectedManager}'s Standings History</span>
        </h3>
        <p className="text-gray-600 text-sm lg:text-base leading-relaxed">
          <span className="hidden sm:inline">
            {selectedManager} has been in the League for {stats.yearsActive} years, 
            and has on average finished the season in the {stats.medianFinish} spot.
          </span>
          <span className="sm:hidden">
            {stats.yearsActive} years • Avg: {getOrdinal(stats.medianFinish)} place
          </span>
        </p>
      </div>

      {/* Chart with responsive height */}
      <div className="h-64 sm:h-80 lg:h-96">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{
              top: 10,
              right: 15,
              left: 10,
              bottom: 10,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis 
              dataKey="year" 
              stroke="#6b7280"
              fontSize={10}
              tickLine={false}
              axisLine={false}
              interval={window.innerWidth < 640 ? 1 : 0} // Show every other year on small screens
              angle={window.innerWidth < 640 ? -45 : 0}
              textAnchor={window.innerWidth < 640 ? 'end' : 'middle'}
              height={window.innerWidth < 640 ? 60 : 30}
            />
            <YAxis 
              stroke="#6b7280"
              fontSize={10}
              tickLine={false}
              axisLine={false}
              reversed={true}
              domain={[1, 12]}
              ticks={[1, 6, 12]}
              width={25}
              label={window.innerWidth >= 640 ? { 
                value: 'Standings Finish', 
                angle: -90, 
                position: 'insideLeft',
                style: { textAnchor: 'middle', fill: '#6b7280', fontSize: '11px' }
              } : undefined}
            />
            <Tooltip content={<CustomTooltip />} />
            
            {/* Background lines for all other managers */}
            {createBackgroundLines()}
            
            {/* Highlighted line for selected manager with mobile-optimized sizing */}
            <Line
              type="monotone"
              dataKey={selectedManager}
              stroke="#8b5cf6"
              strokeWidth={window.innerWidth < 640 ? 3 : 4}
              dot={{ 
                fill: '#8b5cf6', 
                strokeWidth: 2, 
                stroke: '#ffffff',
                r: window.innerWidth < 640 ? 4 : 6
              }}
              activeDot={{ 
                r: window.innerWidth < 640 ? 6 : 8, 
                stroke: '#8b5cf6', 
                strokeWidth: 2,
                fill: '#ffffff'
              }}
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Legend with mobile optimization */}
      <div className="mt-3 sm:mt-4 flex items-center justify-center space-x-4 sm:space-x-6 text-xs sm:text-sm">
        <div className="flex items-center space-x-1 sm:space-x-2">
          <div className="w-3 sm:w-4 h-0.5 bg-purple-600"></div>
          <span className="text-gray-700 font-medium truncate max-w-20 sm:max-w-none">
            {selectedManager}
          </span>
        </div>
        <div className="flex items-center space-x-1 sm:space-x-2">
          <div className="w-3 sm:w-4 h-0.5 bg-gray-300"></div>
          <span className="text-gray-500">Others</span>
        </div>
      </div>
    </div>
  );
};

export default StandingsHistoryChart;